import { Component, HostBinding, Input } from '@angular/core';

/**
 * Reusable button with support for multiple styles and icons.
 */
@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent {
  /** Button type (button, submit, etc) */
  @Input() type = 'button';
  /** Additional CSS classes */
  @Input() classes = '';
  /** Disables the button */
  @Input() disabled = false;
  /** Left icon */
  @Input() startIcon: string;
  /** Right icon */
  @Input() endIcon: string;
  /**
   * Button style variant. Primary.
   *
   * `secondary` is the approved style for a Cancel-like action shown next to a
   * primary action: outlined in the store theme colour, 36px high, 100px wide
   * minimum. Its rule lives in `src/scss/_buttons.scss` as `.btn-secondary-action`
   * so anchor-based call sites can reuse the exact same appearance.
   *
   * `secondary-brand` is the solid secondary-brand-coloured button (admin
   * superadmin actions); its rule lives in `button.component.scss`.
   *
   * `none` adds no variant class — the caller styles the button entirely
   * through `classes` (legacy bootstrap combos such as `btn-link btn-sm`).
   */
  @Input() variant:
    | 'primary'
    | 'theme'
    | 'dark'
    | 'danger'
    | 'link'
    | 'text'
    | 'outline-dark'
    | 'secondary'
    | 'secondary-brand'
    | 'none' = 'theme';
  /** Button size — medium = 42px (same as Cancel / secondary) */
  @Input() size: 'small' | 'medium' | 'large' | 'default' = 'default';
  /** Custom styles */
  @Input() customStyles: { [key: string]: string } = {};
  /** Optional test id forwarded to the rendered native button (for e2e selectors) */
  @Input() dataTestid: string;
  /** Stretch the button to fill the full width of its container. */
  @Input() block = false;
  /** Occupy half the row — used in 2-up wrapping action rows (the container
   *  supplies `display:flex; flex-wrap:wrap; gap`). */
  @Input() half = false;

  @HostBinding('class.app-btn-block') get isBlock(): boolean {
    return this.block;
  }
  @HostBinding('class.app-btn-half') get isHalf(): boolean {
    return this.half;
  }
}
